import { encodeDeployData } from "viem";

import type { ContractArtifact } from "../contracts/loader";
import type { Address, Hash, Hex, PublicClient, WalletClient } from "viem";

export interface DeployResult {
  address: Address;
  txHash: Hash;
  gasUsed: bigint;
}

/**
 * Context passed to every deployment phase.
 *
 * Bundles the viem clients, the deployer account, and a low-level
 * RPC request function for Anvil-specific methods like `anvil_setCode`.
 */
export interface DeployContext {
  publicClient: PublicClient;
  walletClient: WalletClient;
  deployer: Address;
  /** Low-level RPC request for Anvil-specific methods (anvil_setCode, etc.) */
  request: (args: { method: string; params: unknown[] }) => Promise<unknown>;
}

/**
 * Deploy a contract to Anvil and wait for the receipt.
 *
 * Uses `eth_sendTransaction` — Anvil auto-signs for unlocked/impersonated
 * accounts, so no private key is needed.
 */
export async function deployContract(
  publicClient: PublicClient,
  walletClient: WalletClient,
  deployer: Address,
  artifact: ContractArtifact,
  args?: readonly unknown[],
): Promise<DeployResult> {
  let data: Hex;
  if (args && args.length > 0) {
    data = encodeDeployData({
      abi: artifact.abi,
      bytecode: artifact.bytecode,
      args: [...args],
    });
  } else {
    data = artifact.bytecode;
  }

  const txHash = await walletClient.sendTransaction({
    account: deployer,
    data,
    chain: walletClient.chain,
  });

  const receipt = await publicClient.waitForTransactionReceipt({
    hash: txHash,
  });

  if (!receipt.contractAddress) {
    throw new Error(
      `Contract deployment failed: no contract address in receipt (tx: ${txHash})`,
    );
  }

  return {
    address: receipt.contractAddress,
    txHash,
    gasUsed: receipt.gasUsed,
  };
}

/**
 * Deploy a contract with pre-encoded bytecode.
 *
 * Use this for contracts with linked libraries or special encoding
 * where `encodeDeployData` is not applicable (e.g., after manual
 * library linking).
 */
export async function deployRaw(
  publicClient: PublicClient,
  walletClient: WalletClient,
  deployer: Address,
  bytecode: Hex,
): Promise<DeployResult> {
  const txHash = await walletClient.sendTransaction({
    account: deployer,
    data: bytecode,
    chain: walletClient.chain,
  });

  const receipt = await publicClient.waitForTransactionReceipt({
    hash: txHash,
  });

  if (!receipt.contractAddress) {
    throw new Error(
      `Raw deployment failed: no contract address in receipt (tx: ${txHash})`,
    );
  }

  return {
    address: receipt.contractAddress,
    txHash,
    gasUsed: receipt.gasUsed,
  };
}

/**
 * Link a Solidity library into unlinked bytecode.
 *
 * Hardhat/Foundry artifacts contain `__$<hash>$__` placeholders for
 * unlinked library references. This function replaces those placeholders
 * with the deployed library address.
 *
 * @param bytecode  The unlinked bytecode (containing `__$...$__` placeholders)
 * @param libraryName  Fully-qualified library name (e.g., `contracts/libraries/NFTDescriptor.sol:NFTDescriptor`)
 * @param libraryAddress  The deployed library address
 * @returns The linked bytecode ready for deployment
 */
export function linkLibrary(
  bytecode: Hex,
  libraryName: string,
  libraryAddress: Address,
): Hex {
  // Hardhat uses a keccak256 hash of the fully-qualified library name,
  // truncated to 34 hex chars, wrapped in __$...$__
  // Total placeholder length = 40 chars (20 bytes, same as an address)
  const addr = libraryAddress.toLowerCase().replace("0x", "");

  // Find __$<34-hex-chars>$__ patterns and replace with the address
  // The placeholder is always 40 chars: __$ (3) + 34 hex + $__ (3) = 40 total... wait
  // Actually: __$ + 34 hex + $__ = 2 + 1 + 34 + 1 + 2 = 40 chars
  // But we need to find the specific placeholder for this library.
  //
  // Strategy: since we know which library we're linking, compute the
  // placeholder hash the same way Hardhat does, or just find any
  // __$...$__ placeholder (when there's only one library reference).
  const placeholder = `__\\$[0-9a-fA-F]{34}\\$__`;
  const regex = new RegExp(placeholder, "g");

  const linked = bytecode.replace(regex, addr);

  if (linked === bytecode) {
    // No placeholder found — either already linked or wrong library name
    throw new Error(
      `No library placeholder found in bytecode for: ${libraryName}`,
    );
  }

  return linked as Hex;
}
